import { useCallback, useEffect } from "react";
import { api } from "@/lib/api";
import { Scissors, X } from "@phosphor-icons/react";

const shortName = (email) => (email || "").split("@")[0] || "—";

export default function RecentCutsPanel({ open, onOpenChange, cuts = [], onRefresh }) {
  const load = useCallback(() => { onRefresh?.(); }, [onRefresh]);

  useEffect(() => {
    if (!open) return;
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [open, load]);

  return (
    <>
      {!open && (
        <button
          onClick={() => onOpenChange(true)}
          data-testid="recent-cuts-toggle"
          className="fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-slate-900 text-white rounded-l-md pl-2 pr-1.5 py-3 flex flex-col items-center gap-2 shadow-lg hover:bg-slate-800 transition-colors"
          aria-label="Show recent cuts"
        >
          <Scissors size={16} />
          <span className="text-[8px] font-mono tracking-[0.25em] uppercase [writing-mode:vertical-rl] rotate-180">Cuts</span>
        </button>
      )}

      <aside
        data-testid="recent-cuts-panel"
        className={`fixed right-0 top-0 h-screen w-[260px] max-w-[80vw] bg-white border-l border-slate-200 z-40 transform transition-transform duration-200 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Scissors size={16} className="text-slate-700" />
            <h2 className="font-heading font-semibold text-sm text-slate-900">Recent Cuts</h2>
          </div>
          <button onClick={() => onOpenChange(false)} data-testid="recent-cuts-close" className="text-slate-400 hover:text-slate-800" aria-label="Hide recent cuts">
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto h-[calc(100vh-49px)]">
          {cuts.length === 0 ? (
            <div className="p-5 text-sm text-slate-500">No cuts recorded yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {cuts.map((c) => (
                <li key={c.id} className="px-4 py-2.5" data-testid="recent-cut-item">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono text-sm font-semibold tabular-nums text-red-600">−{c.length_m.toFixed(2)}m</div>
                    <div className="font-mono text-[10px] text-slate-400">{new Date(c.cut_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-xs font-medium text-slate-900 mt-1 truncate" data-testid="recent-cut-product">{c.product_name || "—"}</div>
                  {c.roll_sku && <div className="font-mono text-[10px] text-slate-400 truncate">{c.roll_sku}</div>}
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                    {c.customer ? <span>{c.customer} · </span> : null}by <span className="font-medium text-slate-700" data-testid="recent-cut-user">{shortName(c.user_email)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </>
  );
}
